import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { format } from 'date-fns'

import { useAppDispatch } from '@/state'
import {
  getDateTimeFromIsoUTC,
  getDateForTable,
  getFormatNumber,
  getBonusType,
} from '@/utils'
import {
  GlobalUIActions,
  uiGlobalSelector,
} from '@/state/ui/global/slices/index.slices'

import { walletsSelector } from '@/state/wallets/slices/index.slices'
import useTableSort from '@/hooks/table/use-table-sort.hook'
import { dateTimeTableFormat } from '@/constants/date'

import {
  BonusType,
  BonusWalletStatusType,
  CurrencyCodeEnum,
  TimeZoneUTCType,
} from '@/enums'
import { Wallets, WalletLibraryColumnProps } from '@/state/wallets/models'
import { TableHeadCellPropsType } from '@/state/ui/global/models'

import { WalletsStatusLabels } from '@/constants/wallets'
import { useWalletsActions } from './use-wallets-actions.hook'

export const useWalletsTable = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const { deleteWallet, calculateRollover, syncBetsoftBonus } =
    useWalletsActions()

  const { wallets, pagination, isLoading } = useSelector(
    walletsSelector,
    shallowEqual,
  )

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  const headCells: TableHeadCellPropsType<WalletLibraryColumnProps>[] = [
    { id: 'status', label: t('labels.status'), width: 150, sort: true },
    { id: 'bonusName', label: t('labels.name'), width: 180, sort: true },
    {
      id: 'createdAtNumber',
      label: t('wallet.createdAt'),
      width: 100,
      sort: true,
    },
    { id: 'id', label: t('labels.walletId'), sort: true },
    { id: 'playerId', label: t('labels.playerId'), sort: true },
    { id: 'username', label: t('labels.username'), sort: true },
    { id: 'bonusCode', label: t('labels.bonusCode'), width: 120, sort: true },
    {
      id: 'walletTypeLabel',
      label: t('labels.bonusType'),
      width: 120,
      sort: true,
    },
    { id: 'grantedDeposit', label: t('wallet.depositAmount'), sort: true },
    { id: 'grantedAmount', label: t('labels.bonusAmount'), sort: true },
    {
      id: 'freeSpinsGranted',
      label: t('labels.freeSpinsGranted'),
      width: 120,
      sort: true,
    },
    { id: 'bonusBalance', label: t('wallet.bonusBalance'), sort: true },
    {
      id: 'rolloverAmountGranted',
      label: t('labels.rolloverAmount'),
      sort: true,
    },
    // { id: 'rollover', label: t('labels.rolloverCoefficient'), sort: true },
    { id: 'deposiId', label: t('wallet.depositId'), sort: true },
    { id: 'assignedBy', label: t('wallet.grantedBy'), sort: true },
  ]

  const sortedInitialState = 'createdAtNumber'

  const formattedWallets =
    wallets?.map((wallet) => {
      return {
        ...wallet,
        bonusName:
          wallet.bonusName === 'information not available'
            ? t('wallet.manualBonus')
            : wallet.bonusName,
        walletTypeLabel: wallet.walletType
          ? getBonusType(wallet.walletType?.toString())
          : '-',
        createdAt: wallet.createdAt
          ? getDateForTable(
              `${format(
                getDateTimeFromIsoUTC(
                  wallet.createdAt,
                  TimeZoneUTCType['GMT-07 Pacific Time'],
                ),
                dateTimeTableFormat,
              )} PT`,
            )
          : '-',
        createdAtNumber: wallet.createdAt
          ? new Date(wallet.createdAt).getTime()
          : '-',
        grantedDepositText: getFormatNumber(
          wallet.grantedDeposit,
          wallet.currency || CurrencyCodeEnum.USD,
        ),
        freeSpinsGranted:
          wallet.walletType &&
          wallet.walletType.toString() === BonusType.FREE_SPINS
            ? (wallet.freeSpinsGranted ?? 0)
            : '-',
        grantedAmountText:
          wallet.walletType &&
          wallet.walletType.toString() !== BonusType.FREE_SPINS
            ? getFormatNumber(
                wallet.grantedAmount,
                wallet.currency || CurrencyCodeEnum.USD,
              )
            : wallet.grantedAmount.toFixed(2),
        bonusBalance:
          wallet.walletType &&
          wallet.walletType.toString() !== BonusType.FREE_SPINS
            ? // ? wallet.walletType.toString() !== BonusType.FREE_PLAY
              wallet.bonusAmount + wallet.depositAmount + wallet.winningsAmount
            : wallet.bonusAmount,
        // : '-',
        bonusBalanceAtNumber:
          wallet.walletType &&
          wallet.walletType.toString() !== BonusType.FREE_SPINS
            ? // ? wallet.walletType.toString() !== BonusType.FREE_PLAY
              getFormatNumber(
                wallet.bonusAmount +
                  wallet.depositAmount +
                  wallet.winningsAmount,
                wallet.currency || CurrencyCodeEnum.USD,
              )
            : // : getFormatNumber(
              //     wallet.bonusAmount,
              //     wallet.currency || CurrencyCodeEnum.USD,
              //   )
              '-',
        rolloverAmountGranted:
          wallet.walletType &&
          wallet.walletType.toString() !== BonusType.FREE_SPINS
            ? `${getFormatNumber(
                wallet.rolloverAmount,
                wallet.currency || CurrencyCodeEnum.USD,
              )} / ${getFormatNumber(
                wallet.rolloverGranted,
                wallet.currency || CurrencyCodeEnum.USD,
              )}`
            : '-',
      }
    }) || []

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    WalletLibraryColumnProps,
    keyof WalletLibraryColumnProps
  >(formattedWallets, sortedInitialState, 'desc')

  const getXlsFormattedRow = (wallet: Wallets): WalletLibraryColumnProps => {
    return {
      ...wallet,
      status: WalletsStatusLabels[wallet.status as BonusWalletStatusType].label,
      bonusName:
        wallet.bonusName === 'information not available'
          ? t('wallet.manualBonus')
          : wallet.bonusName,
      walletTypeLabel: wallet.walletType
        ? getBonusType(wallet.walletType?.toString())
        : '-',
      createdAtNumber: wallet.createdAt
        ? `${format(
            getDateTimeFromIsoUTC(
              wallet.createdAt,
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-',
      grantedDeposit: getFormatNumber(
        Number(wallet.grantedDeposit),
        wallet.currency || CurrencyCodeEnum.USD,
      ),
      freeSpinsGranted:
        wallet.walletType &&
        wallet.walletType.toString() === BonusType.FREE_SPINS
          ? (wallet.freeSpinsGranted ?? 0)
          : '-',
      grantedAmount:
        wallet.walletType &&
        wallet.walletType.toString() !== BonusType.FREE_SPINS
          ? getFormatNumber(
              Number(wallet.grantedAmount),
              wallet.currency || CurrencyCodeEnum.USD,
            )
          : Number(wallet.grantedAmount).toFixed(2),
      bonusBalance:
        wallet.walletType &&
        wallet.walletType.toString() !== BonusType.FREE_SPINS
          ? // ? wallet.walletType.toString() !== BonusType.FREE_PLAY
            getFormatNumber(
              wallet.bonusAmount + wallet.depositAmount + wallet.winningsAmount,
              wallet.currency || CurrencyCodeEnum.USD,
            )
          : // : getFormatNumber(
            //     wallet.bonusAmount,
            //     wallet.currency || CurrencyCodeEnum.USD,
            //   )
            '-',
      rolloverAmountGranted:
        wallet.walletType &&
        wallet.walletType.toString() !== BonusType.FREE_SPINS
          ? `${getFormatNumber(
              wallet.rolloverAmount,
              wallet.currency || CurrencyCodeEnum.USD,
            )} / ${getFormatNumber(
              wallet.rolloverGranted,
              wallet.currency || CurrencyCodeEnum.USD,
            )}`
          : '-',
    }
  }

  const handleForfeitWallet = (
    anchorPosition: { top: number; left: number },
    walletId: string,
  ) => {
    dispatch(
      GlobalUIActions.setPopoverConfirmAction({
        action: t('messages.forfeit'),
        anchorPosition,
        open: true,
        type: t('wallet.wallet').toLowerCase(),
        handler: () => deleteWallet({ walletId }),
      }),
    )
  }

  const handleCalculateRollover = (
    anchorPosition: { top: number; left: number },
    playerId: string,
  ) => {
    dispatch(
      GlobalUIActions.setPopoverConfirmAction({
        action: t('messages.calculate'),
        anchorPosition,
        open: true,
        type: t('wallet.wallet').toLowerCase(),
        handler: () => calculateRollover({ playerId }),
      }),
    )
  }

  const handleSyncBetsoftBonus = (
    anchorPosition: { top: number; left: number },
    id: string,
  ) => {
    dispatch(
      GlobalUIActions.setPopoverConfirmAction({
        action: t('messages.sync'),
        anchorPosition,
        open: true,
        type: t('wallet.wallet').toLowerCase(),
        handler: () => syncBetsoftBonus({ id }),
      }),
    )
  }

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    pagination,
    isLoading,
    isLoadingFull,
    headCells,
    handleForfeitWallet,
    getXlsFormattedRow,
    handleCalculateRollover,
    handleSyncBetsoftBonus,
  }
}
